import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { getPublicPath } from '@/utils/paths';
import packageJson from '../../package.json';

const Version = () => {
  const v2 =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/v2');
  const buildTime = process.env.REACT_APP_BUILD_TIME;
  const gitSha =
    process.env.REACT_APP_GIT_SHA || process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA;

  const [deployed, setDeployed] = useState(null);
  const [deployErr, setDeployErr] = useState(null);

  useEffect(() => {
    const url = `${getPublicPath()}/version.json`;
    fetch(url, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`${r.status}`);
        }
        return r.json();
      })
      .then(setDeployed)
      .catch((e) => setDeployErr(e.message));
  }, []);

  const bundleRows = [
    { label: 'Application (bundle)', value: `frontend v${packageJson.version}` },
    { label: 'React', value: React.version },
    { label: 'Environnement', value: process.env.NODE_ENV },
  ];
  if (buildTime) {
    bundleRows.push({ label: 'Build (variables)', value: buildTime });
  }
  if (gitSha) {
    bundleRows.push({ label: 'Commit (variables)', value: gitSha });
  }

  const serverRows =
    deployed &&
    [
      { label: 'Application', value: `${deployed.name} v${deployed.version}` },
      { label: 'Déployé le', value: deployed.builtAt },
      deployed.gitSha ? { label: 'Commit', value: deployed.gitSha } : null,
      deployed.mainJs ? { label: 'main.js', value: deployed.mainJs } : null,
    ].filter(Boolean);

  return (
    <main className="bg-white min-h-screen pt-28 pb-16 px-6 sm:px-8">
      <SEO
        title="Version | MN Médecine Esthétique"
        description="Informations sur la version déployée du site MN Médecine Esthétique."
        keywords="version, build"
        noindex
        url={v2 ? '/v2/version' : '/version'}
      />
      <div className="max-w-4xl mx-auto text-gray-800">
        <h1 className="text-3xl sm:text-4xl font-semibold text-[#1e3a5f] mb-2">Version</h1>
        <p className="text-gray-600 mb-6">
          Informations techniques (page non indexée). Le fichier HTML initial est le même pour toutes
          les URLs : pour vérifier le déploiement sans navigateur, utilisez{' '}
          <code className="text-sm bg-gray-100 px-1 rounded">curl …/version.json</code>.
        </p>

        <h2 className="text-lg font-medium text-gray-900 mb-3">Sur le serveur (version.json)</h2>
        {serverRows ? (
          <dl className="space-y-4 mb-10">
            {serverRows.map(({ label, value }) => (
              <div
                key={label}
                className="flex flex-col sm:flex-row sm:gap-8 border-b border-gray-100 pb-4"
              >
                <dt className="font-medium text-gray-600 sm:w-48 shrink-0">{label}</dt>
                <dd className="font-mono text-sm text-gray-900 break-all">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-gray-500 mb-10 text-sm">
            {deployErr
              ? `Fichier version.json indisponible (${deployErr}). En local, lancez un build puis « node scripts/write-version-json.js », ou déployez.`
              : 'Chargement…'}
          </p>
        )}

        <h2 className="text-lg font-medium text-gray-900 mb-3">Dans le bundle JS</h2>
        <dl className="space-y-4 mb-10">
          {bundleRows.map(({ label, value }) => (
            <div
              key={label}
              className="flex flex-col sm:flex-row sm:gap-8 border-b border-gray-100 pb-4"
            >
              <dt className="font-medium text-gray-600 sm:w-48 shrink-0">{label}</dt>
              <dd className="font-mono text-sm text-gray-900 break-all">{value}</dd>
            </div>
          ))}
        </dl>

        <Link to="/" className="text-[#1e3a5f] font-medium hover:underline">
          Retour à l&apos;accueil
        </Link>
      </div>
    </main>
  );
};

export default Version;
